/**
 * Authentication and affiliate settings read from window.__config, which server/server.js serves
 * as /config.js from App Service settings. One Entra app registration serves every hostname, so
 * nothing here varies by affiliate.
 */
export interface AuthRuntimeConfig {
  /** The product's own name, shown as "Powered by …" on affiliate sites. */
  productName: string;
  clientId: string;
  authority: string;
  knownAuthorities: string[];
  apiScopes: string[];
  /**
   * Scopes for native sign-in: the API scope plus the OIDC scopes. Entra returns an id token only
   * when openid is requested, and the native auth SDK rejects a response without one
   * (id_token_missing). The standard redirect flow adds these itself.
   */
  nativeAuthScopes: string[];
  challengeTypes: string[];
  /** Absolute URL of the same-origin native authentication proxy. */
  authApiProxyUrl: string;
  /** Kill switch: when false every sign-in uses the Microsoft-hosted page. */
  nativeAuthEnabled: boolean;
  /** Custom user attribute that records the affiliate a user signed up through. */
  affiliateAttributeName: string;
  /**
   * Boolean user attribute recording that the user accepted the terms. When set, the sign-up form
   * asks for consent; the user flow collects it on the hosted page otherwise.
   */
  termsAttributeName: string;
  /** Every branding asset URL must start with this value. */
  brandingCdnBaseUrl: string;
  /** Hostnames that are never affiliates (the main site), so no resolve request is made for them. */
  resolveSkipHosts: string[];
  apiUrl: string;
}

const DEFAULT_CLIENT_ID = "f5dd406d-f9a4-46b6-b7b3-b27a79df7596";
const DEFAULT_AUTHORITY = "https://agentsintel.ciamlogin.com/agentsintel.onmicrosoft.com";
const DEFAULT_API_SCOPES = "api://AgentsIntel-api/access_as_user";
const DEFAULT_CHALLENGE_TYPES = "password,oob,redirect";
const OIDC_SCOPES = ["openid", "profile", "offline_access"];
const DEFAULT_PROXY_PATH = "/auth-api";
const DEFAULT_TERMS_ATTRIBUTE =
  "extension_adb4f4b7df584756ace920381c51edd6_AgreedToTermsAndConditions";

type RawConfig = Record<string, unknown>;

function text(raw: RawConfig, key: string, fallback = ""): string {
  const value = raw[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : fallback;
}

function list(raw: RawConfig, key: string, fallback = ""): string[] {
  return text(raw, key, fallback)
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return new URL(DEFAULT_AUTHORITY).host;
  }
}

export function getAuthRuntimeConfig(
  raw: RawConfig = (window.__config ?? {}) as RawConfig,
  origin: string = window.location.origin
): AuthRuntimeConfig {
  const authority = text(raw, "AUTH_AUTHORITY", DEFAULT_AUTHORITY).replace(/\/+$/, "");
  const knownAuthorities = list(raw, "AUTH_KNOWN_AUTHORITIES");
  const proxyPath = text(raw, "AUTH_PROXY_PATH", DEFAULT_PROXY_PATH);
  const apiScopes = list(raw, "AUTH_API_SCOPES", DEFAULT_API_SCOPES);

  return {
    productName: text(raw, "NAME", "AGENTS INTEL"),
    clientId: text(raw, "AUTH_CLIENT_ID", DEFAULT_CLIENT_ID),
    authority,
    knownAuthorities: knownAuthorities.length > 0 ? knownAuthorities : [hostOf(authority)],
    apiScopes,
    nativeAuthScopes: [...apiScopes, ...OIDC_SCOPES.filter((scope) => !apiScopes.includes(scope))],
    challengeTypes: list(raw, "AUTH_CHALLENGE_TYPES", DEFAULT_CHALLENGE_TYPES),
    authApiProxyUrl: /^https?:\/\//i.test(proxyPath)
      ? proxyPath
      : `${origin}${proxyPath.startsWith("/") ? "" : "/"}${proxyPath}`,
    nativeAuthEnabled: text(raw, "NATIVE_AUTH_ENABLED").toLowerCase() === "true",
    affiliateAttributeName: text(raw, "AFFILIATE_ATTRIBUTE_NAME"),
    termsAttributeName: text(raw, "SIGNUP_TERMS_ATTRIBUTE_NAME", DEFAULT_TERMS_ATTRIBUTE),
    brandingCdnBaseUrl: text(raw, "BRANDING_CDN_BASE_URL").replace(/\/+$/, ""),
    resolveSkipHosts: list(raw, "AFFILIATE_RESOLVE_SKIP_HOSTS").map((host) => host.toLowerCase()),
    apiUrl: text(raw, "API_URL").replace(/\/+$/, ""),
  };
}
